import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiMail, FiLock, FiEye, FiEyeOff, FiArrowRight } from 'react-icons/fi';
import { validateEmail } from '@crm/shared';
import authService from '../../services/authService';

export default function Login() {
  const navigate = useNavigate();
  const [form, setForm]               = useState({ email: '', password: '', rememberMe: false });
  const [errors, setErrors]           = useState({});
  const [serverError, setServerError] = useState('');
  const [loading, setLoading]         = useState(false);
  const [showPass, setShowPass]       = useState(false);
  const [focused, setFocused]         = useState('');

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm(p => ({ ...p, [name]: type === 'checkbox' ? checked : value }));
    if (errors[name]) setErrors(p => ({ ...p, [name]: '' }));
    if (serverError) setServerError('');
  };

  const validate = () => {
    const e = {};
    if (!form.email)                     e.email    = 'Email is required';
    else if (!validateEmail(form.email)) e.email    = 'Enter a valid email address';
    if (!form.password)                  e.password = 'Password is required';
    else if (form.password.length < 4)   e.password = 'Minimum 4 characters';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setServerError('');
    if (!validate()) return;
    setLoading(true);
    try {
      const result = await authService.login(form.email, form.password, form.rememberMe);
      if (result.success) navigate('/dashboard', { replace: true });
      else setServerError(result.error || 'Invalid credentials. Please try again.');
    } catch {
      setServerError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const inp = (name) => ({
    width: '100%', height: 48, padding: '0 14px 0 44px',
    border: `1.5px solid ${errors[name] ? '#DC2626' : focused === name ? '#0D3B5E' : '#D1D5DB'}`,
    borderRadius: 10, fontSize: 15, color: '#111827',
    background: focused === name ? '#fff' : '#F9FAFB',
    outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit', transition: 'all 0.15s',
    boxShadow: focused === name ? `0 0 0 3px ${errors[name] ? 'rgba(220,38,38,0.10)' : 'rgba(13,59,94,0.10)'}` : 'none',
  });

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&display=swap');
        *, *::before, *::after { box-sizing: border-box; }
        html, body, #root { margin: 0; padding: 0; height: auto; min-height: 100%; }
        body { background: #EEF2F7; }
        @keyframes cfSpin { to { transform: rotate(360deg); } }
        @keyframes cfRise { from { opacity:0; transform:translateY(18px); } to { opacity:1; transform:translateY(0); } }
        @keyframes cfFade { from { opacity:0; } to { opacity:1; } }
        .cf-right { animation: cfRise 0.4s cubic-bezier(0.16,1,0.3,1) both 0.05s; }
        .cf-left  { animation: cfFade 0.5s ease both; }
        .cf-btn:hover:not(:disabled) { filter: brightness(1.12) !important; transform: translateY(-1px) !important; }
        .cf-btn:active:not(:disabled) { transform: translateY(0) !important; filter: brightness(1) !important; }

        /* ── Responsive layout ── */
        .login-wrapper {
          display: flex;
          width: 100vw;
          font-family: 'Inter', -apple-system, sans-serif;
        }
        /* Left panel: hidden on mobile, shown on desktop */
        .login-left {
          width: 46%;
          flex-shrink: 0;
          background: linear-gradient(150deg, #03111F 0%, #071E36 35%, #0D3B5E 100%);
          position: relative;
          overflow: hidden;
          display: flex;
          flex-direction: column;
        }
        /* Right panel: full width on mobile */
        .login-right {
          flex: 1;
          display: flex;
          align-items: center;
          justify-content: center;
          background: #EEF2F7;
          padding: 32px;
          overflow-y: auto;
          min-height: 100vh;
          box-sizing: border-box;
        }
        .login-card {
          background: #fff;
          border-radius: 20px;
          padding: 38px 42px;
          box-shadow: 0 2px 8px rgba(0,0,0,0.06), 0 16px 48px rgba(0,0,0,0.11);
          border: 1px solid #E5E7EB;
          width: 100%;
          max-width: 420px;
        }
        /* Mobile banner shown only on small screens */
        .mobile-brand-bar {
          display: none;
          align-items: center;
          gap: 12px;
          background: linear-gradient(135deg, #071E36, #0D3B5E);
          padding: 16px 20px;
          border-radius: 14px;
          margin-bottom: 20px;
          border: 1px solid rgba(255,255,255,0.10);
        }

        @media (max-width: 767px) {
          .login-left { display: none !important; }
          .login-right {
            padding: 20px 16px 24px !important;
            align-items: flex-start !important;
            justify-content: flex-start !important;
            min-height: unset !important;
            height: auto !important;
          }
          .login-card {
            padding: 24px 20px;
            border-radius: 16px;
            max-width: 100%;
          }
          .mobile-brand-bar { display: flex !important; }
          .login-heading { font-size: 24px !important; }
          .login-subheading { font-size: 13px !important; }
          .login-right-inner { width: 100% !important; }
        }

        @media (min-width: 768px) and (max-width: 1023px) {
          .login-left { width: 42%; }
          .login-right { padding: 24px; }
          .login-card { padding: 32px 28px; }
        }
      `}</style>

      <div className="login-wrapper">

        {/* ══════════════════════════════
            LEFT PANEL (desktop only)
        ══════════════════════════════ */}
        <div className="cf-left login-left">
          {/* Decorations */}
          <div style={{ position:'absolute', inset:0, pointerEvents:'none' }}>
            <div style={{ position:'absolute', inset:0, backgroundImage:'linear-gradient(rgba(255,255,255,0.03) 1px,transparent 1px),linear-gradient(90deg,rgba(255,255,255,0.03) 1px,transparent 1px)', backgroundSize:'48px 48px' }}/>
            <div style={{ position:'absolute', top:'-20%', left:'-10%', width:'60%', height:'60%', borderRadius:'50%', background:'radial-gradient(circle, rgba(245,166,35,0.13) 0%, transparent 65%)' }}/>
            <div style={{ position:'absolute', bottom:'-25%', right:'-15%', width:'70%', height:'70%', borderRadius:'50%', background:'radial-gradient(circle, rgba(0,100,200,0.17) 0%, transparent 65%)' }}/>
            <div style={{ position:'absolute', top:0, left:0, right:0, height:4, background:'linear-gradient(90deg,#F5A623 0%,#FBBF4A 50%,transparent 100%)' }}/>
          </div>

          <div style={{ position:'relative', zIndex:10, display:'flex', flexDirection:'column', height:'100%', padding:'28px 40px 24px', overflow:'hidden' }}>

            {/* Logo */}
            <div style={{ display:'flex', alignItems:'center', gap:14, marginBottom:24, flexShrink:0 }}>
              <div style={{ width:52, height:52, borderRadius:13, overflow:'hidden', flexShrink:0, background:'rgba(255,255,255,0.08)', boxShadow:'0 4px 20px rgba(0,0,0,0.35)' }}>
                <img src="/logo.jpeg" alt="Credi Flex" style={{ width:'100%', height:'100%', objectFit:'cover', display:'block' }}/>
              </div>
              <div>
                <div style={{ fontSize:22, fontWeight:900, color:'#fff', letterSpacing:'-0.03em', lineHeight:1.1 }}>Credi Flex</div>
                <div style={{ fontSize:12, color:'rgba(255,255,255,0.40)', fontWeight:500, marginTop:2 }}>Consultants LLP</div>
              </div>
            </div>

            {/* Hero */}
            <div style={{ flex:1, display:'flex', flexDirection:'column', justifyContent:'center', minHeight:0 }}>
              <div style={{ display:'inline-flex', alignItems:'center', gap:7, background:'rgba(245,166,35,0.15)', border:'1px solid rgba(245,166,35,0.38)', borderRadius:999, padding:'4px 13px', marginBottom:18, width:'fit-content' }}>
                <div style={{ width:7, height:7, borderRadius:'50%', background:'#F5A623', boxShadow:'0 0 6px #F5A623' }}/>
                <span style={{ fontSize:10.5, fontWeight:800, color:'#FBBF4A', letterSpacing:'0.08em', textTransform:'uppercase' }}>CRM &amp; Lead Platform</span>
              </div>

              <div style={{ fontSize:42, fontWeight:900, color:'#fff', letterSpacing:'-0.05em', lineHeight:1.0, marginBottom:4 }}>Close More</div>
              <div style={{ fontSize:42, fontWeight:900, letterSpacing:'-0.05em', lineHeight:1.0, marginBottom:16, background:'linear-gradient(130deg,#F5A623 0%,#FBBF4A 45%,#FDE68A 100%)', WebkitBackgroundClip:'text', WebkitTextFillColor:'transparent' }}>Deals. Faster.</div>

              <p style={{ fontSize:13.5, color:'rgba(255,255,255,0.50)', lineHeight:1.6, maxWidth:320, margin:'0 0 22px' }}>
                Built for lending teams — track every lead from first touch to disbursement.
              </p>

              <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:8, marginBottom:20 }}>
                {[{ val:'10×', lbl:'Faster' }, { val:'99.9%', lbl:'Uptime' }, { val:'360°', lbl:'Lead View' }].map(s=>(
                  <div key={s.lbl} style={{ background:'rgba(255,255,255,0.07)', border:'1px solid rgba(255,255,255,0.10)', borderRadius:11, padding:'12px 10px', textAlign:'center' }}>
                    <div style={{ fontSize:22, fontWeight:900, color:'#fff', letterSpacing:'-0.04em', lineHeight:1, marginBottom:4 }}>{s.val}</div>
                    <div style={{ fontSize:10, color:'rgba(255,255,255,0.40)', fontWeight:600, letterSpacing:'0.04em', textTransform:'uppercase' }}>{s.lbl}</div>
                  </div>
                ))}
              </div>

              <div style={{ display:'flex', flexDirection:'column', gap:9 }}>
                {[
                  { icon:'⚡', text:'End-to-end lead lifecycle' },
                  { icon:'📅', text:'Smart follow-up & workflow tracking' },
                  { icon:'📊', text:'Real-time analytics & dashboards' },
                  { icon:'🔐', text:'Role-based access control' },
                ].map((f,i)=>(
                  <div key={i} style={{ display:'flex', alignItems:'center', gap:11 }}>
                    <div style={{ width:30, height:30, borderRadius:8, flexShrink:0, background:'rgba(245,166,35,0.13)', border:'1px solid rgba(245,166,35,0.25)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:14 }}>{f.icon}</div>
                    <span style={{ fontSize:13, color:'rgba(255,255,255,0.72)', fontWeight:500 }}>{f.text}</span>
                  </div>
                ))}
              </div>
            </div>

            <div style={{ flexShrink:0, paddingTop:16, borderTop:'1px solid rgba(255,255,255,0.08)', fontSize:11, color:'rgba(255,255,255,0.22)', marginTop:16 }}>
              © {new Date().getFullYear()} Credi Flex Consultants LLP · v1.0
            </div>
          </div>
        </div>

        {/* ══════════════════════════════
            RIGHT PANEL
        ══════════════════════════════ */}
        <div className="login-right">
          <div className="cf-right login-right-inner" style={{ width:'100%', maxWidth:420 }}>

            {/* ── Mobile-only brand bar (shows on phones instead of left panel) ── */}
            <div className="mobile-brand-bar">
              <div style={{ width:44, height:44, borderRadius:11, overflow:'hidden', flexShrink:0, background:'rgba(255,255,255,0.10)', boxShadow:'0 2px 12px rgba(0,0,0,0.30)' }}>
                <img src="/logo.jpeg" alt="Credi Flex" style={{ width:'100%', height:'100%', objectFit:'cover', display:'block' }}/>
              </div>
              <div>
                <div style={{ fontSize:18, fontWeight:900, color:'#fff', letterSpacing:'-0.03em', lineHeight:1.1 }}>Credi Flex</div>
                <div style={{ fontSize:11, color:'rgba(255,255,255,0.45)', fontWeight:500 }}>CRM &amp; Lead Platform</div>
              </div>
              <div style={{ marginLeft:'auto', display:'flex', alignItems:'center', gap:5, background:'rgba(245,166,35,0.18)', border:'1px solid rgba(245,166,35,0.35)', borderRadius:999, padding:'4px 10px' }}>
                <div style={{ width:6, height:6, borderRadius:'50%', background:'#22C55E' }}/>
                <span style={{ fontSize:10, fontWeight:700, color:'#FBBF4A', letterSpacing:'0.06em' }}>LIVE</span>
              </div>
            </div>

            {/* ── Login Card ── */}
            <div className="login-card">

              {/* heading */}
              <div style={{ marginBottom:24 }}>
                <div className="login-heading" style={{ fontSize:28, fontWeight:900, color:'#0D1B2A', letterSpacing:'-0.04em', lineHeight:1.15, marginBottom:6 }}>Welcome back 👋</div>
                <div className="login-subheading" style={{ fontSize:14, color:'#6B7280' }}>Sign in to your Credi Flex workspace</div>
              </div>

              {/* server error */}
              {serverError && (
                <div style={{ display:'flex', alignItems:'flex-start', gap:10, background:'#FEF2F2', border:'1px solid #FECACA', borderLeft:'4px solid #DC2626', borderRadius:10, padding:'11px 14px', marginBottom:20, fontSize:13, color:'#991B1B', fontWeight:500, lineHeight:1.4 }}>
                  <span style={{ fontSize:15, marginTop:1, flexShrink:0 }}>⚠</span>{serverError}
                </div>
              )}

              <form onSubmit={handleSubmit} noValidate>

                {/* email */}
                <div style={{ marginBottom:16 }}>
                  <label style={{ display:'block', fontSize:13, fontWeight:700, color:'#111827', marginBottom:7 }}>Email address</label>
                  <div style={{ position:'relative' }}>
                    <FiMail size={16} style={{ position:'absolute', left:13, top:'50%', transform:'translateY(-50%)', color:focused==='email'?'#0D3B5E':'#9CA3AF', pointerEvents:'none', transition:'color 0.15s' }}/>
                    <input type="email" name="email" value={form.email} onChange={handleChange} disabled={loading}
                      autoComplete="email" placeholder="you@crediflex.com"
                      style={inp('email')} onFocus={()=>setFocused('email')} onBlur={()=>setFocused('')}/>
                  </div>
                  {errors.email && <div style={{ fontSize:12, color:'#DC2626', fontWeight:600, marginTop:5 }}>⚠ {errors.email}</div>}
                </div>

                {/* password */}
                <div style={{ marginBottom:14 }}>
                  <label style={{ display:'block', fontSize:13, fontWeight:700, color:'#111827', marginBottom:7 }}>Password</label>
                  <div style={{ position:'relative' }}>
                    <FiLock size={16} style={{ position:'absolute', left:13, top:'50%', transform:'translateY(-50%)', color:focused==='password'?'#0D3B5E':'#9CA3AF', pointerEvents:'none', transition:'color 0.15s' }}/>
                    <input type={showPass?'text':'password'} name="password" value={form.password} onChange={handleChange} disabled={loading}
                      autoComplete="current-password" placeholder="Enter your password"
                      style={{ ...inp('password'), paddingRight:46 }}
                      onFocus={()=>setFocused('password')} onBlur={()=>setFocused('')}/>
                    <button type="button" onClick={()=>setShowPass(p=>!p)}
                      style={{ position:'absolute', right:11, top:'50%', transform:'translateY(-50%)', background:'transparent', border:'none', cursor:'pointer', color:'#9CA3AF', display:'flex', alignItems:'center', padding:4 }}>
                      {showPass ? <FiEyeOff size={16}/> : <FiEye size={16}/>}
                    </button>
                  </div>
                  {errors.password && <div style={{ fontSize:12, color:'#DC2626', fontWeight:600, marginTop:5 }}>⚠ {errors.password}</div>}
                </div>

                {/* remember + forgot */}
                <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:22, flexWrap:'wrap', gap:8 }}>
                  <label style={{ display:'flex', alignItems:'center', gap:8, fontSize:13, color:'#6B7280', fontWeight:500, cursor:'pointer', userSelect:'none' }}>
                    <input type="checkbox" name="rememberMe" checked={form.rememberMe} onChange={handleChange} disabled={loading}
                      style={{ width:16, height:16, cursor:'pointer', accentColor:'#0D3B5E' }}/>
                    Keep me signed in
                  </label>
                  <button type="button" style={{ fontSize:13, fontWeight:700, color:'#0D3B5E', background:'transparent', border:'none', cursor:'pointer', padding:0, flexShrink:0 }}>
                    Forgot password?
                  </button>
                </div>

                {/* submit */}
                <button type="submit" disabled={loading} className="cf-btn" style={{
                  width:'100%', height:52,
                  display:'flex', alignItems:'center', justifyContent:'center', gap:10,
                  background:'linear-gradient(135deg, #0D3B5E 0%, #155E8F 100%)',
                  color:'#fff', border:'none', borderRadius:12,
                  fontSize:15, fontWeight:800, cursor: loading ? 'not-allowed' : 'pointer', fontFamily:'inherit',
                  position:'relative', overflow:'hidden',
                  boxShadow:'0 6px 20px rgba(13,59,94,0.38)',
                  transition:'all 0.15s',
                }}>
                  <div style={{ position:'absolute', bottom:0, left:0, right:0, height:3, background:'linear-gradient(90deg,#F5A623,#FBBF4A)' }}/>
                  {loading
                    ? <><div style={{ width:18, height:18, borderRadius:'50%', border:'2.5px solid rgba(255,255,255,0.28)', borderTopColor:'#fff', animation:'cfSpin 0.65s linear infinite', flexShrink:0 }}/> Signing in…</>
                    : <><span>Sign in to Dashboard</span><FiArrowRight size={16}/></>
                  }
                </button>
              </form>

              {/* trust bar */}
              <div style={{ display:'flex', alignItems:'center', justifyContent:'center', gap:14, marginTop:20, paddingTop:18, borderTop:'1px solid #F3F4F6', flexWrap:'wrap' }}>
                <div style={{ display:'flex', alignItems:'center', gap:6, fontSize:11.5, color:'#9CA3AF', fontWeight:500 }}>
                  <div style={{ width:7, height:7, borderRadius:'50%', background:'#22C55E', flexShrink:0 }}/>
                  All systems operational
                </div>
                <span style={{ color:'#D1D5DB' }}>·</span>
                <div style={{ fontSize:11.5, color:'#9CA3AF', fontWeight:500 }}>🔒 256-bit SSL</div>
              </div>
            </div>

            <div style={{ marginTop:12, marginBottom:0, paddingBottom:0, textAlign:'center', fontSize:11.5, color:'#9CA3AF' }}>
              © {new Date().getFullYear()} Credi Flex Consultants LLP · All rights reserved.
            </div>
          </div>
        </div>

      </div>
    </>
  );
}
